/**
 * Celebration Overlay
 * ───────────────────
 * Full-screen celebration for streak milestones and level-ups. Mounted once in
 * router.tsx (next to NotificationService) so it fires on any screen.
 *
 * Consumes the store's pending signals: milestone shows first; if a level-up
 * is also pending (common — milestones grant bonus XP), it renders after the
 * milestone is dismissed. CSS-only confetti, skipped under
 * prefers-reduced-motion. One unified card for all milestone tiers at launch;
 * the per-milestone `celebration` type stays in data for future treatments.
 */
import { useStreakStore, MILESTONES } from '@/lib/streak-store';
import { XP_PER_CHECKIN, XP_MILESTONE_BONUS } from '@/lib/xp';
import { useTheme } from '@/lib/theme-context';
import { useI18n } from '@/lib/i18n';
import { font } from '@/design-system/tokens';
import { StreakBadgeIcon, MILESTONE_COLORS, FireIcon } from '@/components/icons';

const CONFETTI_COUNT = 30;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function Confetti({ palette }: { palette: string[] }) {
  if (prefersReducedMotion()) return null;
  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-8vh) rotate(0deg); opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(108vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
      {Array.from({ length: CONFETTI_COUNT }, (_, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: 0,
          // Deterministic spread — no randomness needed for a one-shot burst
          left: `${(i * 37) % 100}%`,
          width: 6,
          height: 10,
          borderRadius: 2,
          background: palette[i % palette.length],
          animation: `confetti-fall ${2.2 + ((i * 13) % 10) / 8}s ease-in ${(i % 10) * 0.12}s forwards`,
          opacity: 0,
        }} />
      ))}
    </>
  );
}

export default function CelebrationOverlay() {
  const { colors } = useTheme();
  const { t } = useI18n();
  const pendingMilestone = useStreakStore(s => s.pendingMilestoneCelebration);
  const pendingLevelUp = useStreakStore(s => s.pendingLevelUp);
  const clearMilestoneCelebration = useStreakStore(s => s.clearMilestoneCelebration);
  const clearLevelUp = useStreakStore(s => s.clearLevelUp);
  const streakCount = useStreakStore(s => s.streakCount);

  // Milestone takes priority; level-up renders after it is dismissed
  const mode = pendingMilestone !== null ? 'milestone' : pendingLevelUp !== null ? 'level' : null;
  if (mode === null) return null;

  const milestone = mode === 'milestone' ? MILESTONES.find(m => m.day === pendingMilestone) : undefined;
  const accentColor = milestone ? (MILESTONE_COLORS[milestone.day] ?? colors.accent) : colors.accent;
  const palette = [colors.accent, colors.green, colors.yellow, colors.blue, colors.cyan];

  const dismiss = () => {
    if (mode === 'milestone') clearMilestoneCelebration();
    else clearLevelUp();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
        overflow: 'hidden', padding: 24,
      }}
    >
      <Confetti palette={palette} />

      <style>{`
        @keyframes celebration-pop {
          0%   { transform: scale(0.7); opacity: 0; }
          70%  { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 340,
        background: colors.bgCard,
        border: `1px solid ${accentColor}40`,
        borderRadius: 20, padding: '32px 24px 24px',
        textAlign: 'center',
        animation: prefersReducedMotion() ? undefined : 'celebration-pop 0.35s ease-out',
      }}>
        {mode === 'milestone' && milestone ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <StreakBadgeIcon day={milestone.day} size={88} color={accentColor} />
            </div>
            <div style={{
              fontSize: 10, color: accentColor, textTransform: 'uppercase',
              letterSpacing: '0.14em', fontFamily: font.mono, marginBottom: 8,
            }}>
              {t('gami.badgeUnlocked')}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 6px', color: colors.textPrimary }}>
              {milestone.label}
            </h2>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, color: colors.textSecondary, fontFamily: font.mono, marginBottom: 14,
            }}>
              <FireIcon size={13} color={accentColor} />
              {t('gami.dayStreak', { n: streakCount })}
            </div>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 6px', lineHeight: 1.6 }}>
              {milestone.reward}
            </p>
            <div style={{ fontSize: 12, color: colors.green, fontFamily: font.mono, fontWeight: 600, marginBottom: 20 }}>
              +{XP_PER_CHECKIN + XP_MILESTONE_BONUS} XP
            </div>
          </>
        ) : (
          <>
            <div style={{
              width: 88, height: 88, margin: '0 auto 16px', borderRadius: '50%',
              border: `2.5px solid ${colors.accent}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 8, color: colors.textMuted, fontFamily: font.mono, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                {t('gami.level')}
              </span>
              <span style={{ fontSize: 32, fontWeight: 700, color: colors.accent, fontFamily: font.mono, lineHeight: 1 }}>
                {pendingLevelUp}
              </span>
            </div>
            <div style={{
              fontSize: 10, color: colors.accent, textTransform: 'uppercase',
              letterSpacing: '0.14em', fontFamily: font.mono, marginBottom: 8,
            }}>
              {t('gami.levelUp')}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 14px', color: colors.textPrimary }}>
              {t('gami.levelReached', { n: pendingLevelUp ?? 0 })}
            </h2>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 20px', lineHeight: 1.6 }}>
              {t('gami.levelUpBody')}
            </p>
          </>
        )}

        <button
          onClick={dismiss}
          style={{
            width: '100%', padding: '13px 16px',
            background: accentColor, border: 'none', borderRadius: 12,
            fontSize: 14, fontWeight: 600, color: colors.bgPrimary, cursor: 'pointer',
          }}
        >
          {t('gami.continue')}
        </button>
      </div>
    </div>
  );
}
